import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Zap } from 'lucide-react';
import { openCalendly } from '../lib/calendly';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass backdrop-blur-xl border-b border-white/10' 
          : 'glass-dark backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-2"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-neon-blue rounded-xl flex items-center justify-center neon-glow">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-semibold gradient-text">DeploAI</span>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <a href="#capabilities" className="text-gray-300 hover:text-white transition-colors duration-200">
              Capabilities
            </a>
            <a href="#ai-operations" className="text-gray-300 hover:text-white transition-colors duration-200">
              AI Operations
            </a>
            <a href="#why" className="text-gray-300 hover:text-white transition-colors duration-200">
              Why DeploAI
            </a>
            <a href="#faq" className="text-gray-300 hover:text-white transition-colors duration-200">
              FAQ
            </a>
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={() => openCalendly('trial')}
              className="premium-button"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            className="lg:hidden p-2 text-gray-300 hover:text-white transition-colors duration-200"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-dark rounded-lg mt-2 mb-4"
          >
            <div className="px-4 py-6 space-y-4">
              <a href="#capabilities" className="block text-gray-300 hover:text-white transition-colors duration-200">
                Capabilities
              </a>
              <a href="#ai-operations" className="block text-gray-300 hover:text-white transition-colors duration-200">
                AI Operations
              </a>
              <a href="#why" className="block text-gray-300 hover:text-white transition-colors duration-200">
                Why DeploAI
              </a>
              <a href="#faq" className="block text-gray-300 hover:text-white transition-colors duration-200">
                FAQ
              </a>
              <div className="pt-4 space-y-3">
                <button
                  onClick={() => openCalendly('trial')}
                  className="w-full premium-button"
                >
                  Get Started
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

export default Header; 